/**
 * SectionTable Component - Property Matrix Display
 *
 * Displays property listings in matrix format based on community type:
 *
 * UNIFORM HOME TYPE COMMUNITIES:
 * - All properties are the same type (all SFH, all Condo, etc.)
 * - Without subdivisions: 4 matrices total (FOR SALE, FOR RENT, SOLD, RENTED)
 * - With subdivisions: 4 matrices PER SUBDIVISION
 *
 * NON-UNIFORM (MIXED) HOME TYPE COMMUNITIES:
 * - Mix of different property types (SFH, Condo, Townhome, Villa, Coach Home)
 * - Usually have multiple subdivisions
 * - 4 matrices PER SUBDIVISION
 *
 * Matrix columns adjust based on:
 * - Single home type vs multiple home types (adds HOME TYPE column)
 * - Condo-only vs other types (hides CVRD GAR and PRVT POOL for condos)
 * - Sale/Sold vs Rent/Rented (adds TERM column for rentals)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CustomTable, { AlwaysVisibleScrollbar } from "./table";
import { URL } from "../utils/axios";
import { usePageHeaderByKey } from "../hooks/usePageHeaderBykey";
import { useSiteSettings } from "../hooks/useSite";
import axiosInstance from "../utils/axios";
import { convertDate } from "../helper";

const PROMOTION_STYLE_BY_SLOT = {
  1: {
    backgroundColor: "#fff27a",
    color: "#000000",
  },
  2: {
    backgroundColor: "#fff27a",
    color: "#000000",
  },
  3: {
    backgroundColor: "#fff27a",
    color: "#000000",
  },
  4: {
    backgroundColor: "#fff27a",
    color: "#000000",
  },
  5: {
    backgroundColor: "#fff27a",
    color: "#000000",
  },
};

const ACTIVE_FILTER_BUTTON_CLASS =
  "bg-red-600 text-white border-red-600 hover:bg-red-700";
const INACTIVE_FILTER_BUTTON_CLASS =
  "bg-white text-red-600 border-black hover:bg-gray-50";

/* Filter drop-downs mirror INACTIVE_FILTER_BUTTON_CLASS (white fill, bold red text,
 * black border) so the selected option reads as loud as the filter buttons.
 * The select is auto-width: a native select with width:auto sizes itself to its
 * longest option, so it never stretches past the text it holds. */
const FILTER_DROPDOWN_ROW_CLASS =
  "flex flex-row flex-wrap justify-center items-center gap-x-4 gap-y-1.5";
const FILTER_DROPDOWN_GROUP_CLASS = "flex flex-row items-center gap-1.5";
const FILTER_DROPDOWN_LABEL_CLASS =
  "text-xs font-extrabold uppercase text-black leading-tight whitespace-nowrap";
const FILTER_DROPDOWN_SELECT_CLASS =
  "w-auto max-w-full border-2 border-black rounded bg-white px-1.5 py-0.5 text-xs font-bold text-red-600";

// Which CW listing statuses each matrix shows. The admin sets this per community
// (Matrices tab); these defaults are what the site did when the rule lived here as
// a hard-coded condition, and they apply until the config arrives or if it fails.
const DEFAULT_MATRIX_STATUSES = {
  SALE: ["ACTIVE", "PENDING"],
  RENT: ["ACTIVE", "PENDING"],
  SOLD: ["SOLD"],
  RENTED: ["RENTED"],
};

// The server names the matrices FOR_SALE / FOR_RENT; internally they are SALE / RENT.
const MATRIX_KEY_BY_CONFIG_KEY = {
  FOR_SALE: "SALE",
  FOR_RENT: "RENT",
  SOLD: "SOLD",
  RENTED: "RENTED",
};

// The four matrices, in the order they render on the page.
const MATRIX_ORDER = ["SALE", "RENT", "SOLD", "RENTED"];

// A matrix only ever draws from its own side of the community.
const MATRIX_LISTING_SIDE = {
  SALE: "sale",
  SOLD: "sale",
  RENT: "rental",
  RENTED: "rental",
};

/** Time-range options for the SOLD / RENTED matrices (admin-toggled per matrix via
 *  matrixSoldDateRangeMenu / matrixRentedDateRangeMenu). */
const DATE_RANGE_OPTIONS = [
  { label: "ALL", value: "ALL" },
  { label: "CURRENT YEAR", value: "THIS_YEAR" },
  { label: "LAST 1 YEAR", value: "LAST_1_YEAR" },
  { label: "LAST 2 YEARS", value: "LAST_2_YEARS" },
];

/** Which date field each time-windowed matrix filters on. */
const DATE_RANGE_FIELD_BY_STATUS = {
  SOLD: "date_became_sold",
  RENTED: "date_became_rented",
};

/** Per-matrix admin menu keys (CommunityAdminConfig) for the above-table filters.
 *  Each control is rendered above its own matrix and filters only that matrix. */
const DATE_RANGE_MENU_KEY_BY_STATUS = {
  SOLD: "matrixSoldDateRangeMenu",
  RENTED: "matrixRentedDateRangeMenu",
};
const SUBDIVISION_MENU_KEY_BY_STATUS = {
  SOLD: "matrixSoldSubdivisionMenu",
  RENTED: "matrixRentedSubdivisionMenu",
};

/** All property fields we collect (Excel/DB). Display in modal when non-empty. Order: position ASC. */
const PROPERTY_DISPLAY_FIELDS = [
  { key: "mls_number", label: "MLS #", position: 0 },
  { key: "cw_listing_status", label: "CW Listing Status", position: 1 },
  { key: "mls_listing_status", label: "Listing Status", position: 1.5 },
  { key: "listing_type", label: "Listing Type", position: 2 },
  { key: "home_type", label: "Property Type", position: 3 },
  { key: "address", label: "Address", position: 4, computed: true },
  { key: "street_number", label: "Building No", position: 5 },
  { key: "compass_direction", label: "Compass Direction", position: 6 },
  { key: "street_name", label: "Street Name", position: 7 },
  { key: "post_street_direction", label: "Post Street Direction", position: 8 },
  { key: "unit_number", label: "Unit #", position: 9 },
  { key: "city", label: "City", position: 10 },
  { key: "state", label: "State", position: 11 },
  { key: "zip_code", label: "Zip Code", position: 12 },
  { key: "county_name", label: "County", position: 13 },
  { key: "short_address", label: "Short Address", position: 15 },
  { key: "very_short_address", label: "Very Short Address", position: 16 },
  { key: "county_property_id", label: "County Property ID", position: 17 },
  { key: "_subdivision", label: "Subdivision", position: 18, computed: true },
  { key: "bedrooms", label: "Bedrooms", position: 20 },
  { key: "bathrooms", label: "Total Bathrooms", position: 21 },
  { key: "half_bathrooms", label: "Half Bathrooms", position: 22 },
  { key: "sf_under_ac", label: "SF Under AC", position: 23 },
  { key: "year_built", label: "Year Built", position: 24 },
  { key: "private_pool", label: "Private Pool", position: 25 },
  { key: "community_pool", label: "Community Pool", position: 26 },
  { key: "garage", label: "Garage", position: 27 },
  { key: "water_view", label: "Water View", position: 28 },
  { key: "furniture", label: "Furniture", position: 30 },
  { key: "pets", label: "Pets", position: 31 },
  {
    key: "price_or_rent",
    label: "Asking Price / Rent",
    position: 40,
    format: "price",
  },
  { key: "mls_sold_price", label: "Sold Price", position: 41, format: "price" },
  { key: "rented_price", label: "Rented Price", position: 42, format: "price" },
  { key: "original_asking_price", label: "Original Asking Price", position: 40.5, format: "price" },
  { key: "original_asking_rent", label: "Original Asking Rent", position: 40.75, format: "price" },
  { key: "real_estate_tax", label: "Real Estate Tax", position: 41.5, format: "price" },
  { key: "county_assessed_value", label: "County Assessed Value", position: 41.75, format: "price" },
  { key: "previous_sold_price", label: "Previous Sold Price", position: 42.5, format: "price" },
  { key: "previous_sold_date", label: "Previous Sold Date", position: 42.75, format: "date" },
  { key: "dom", label: "DOM", position: 43.75, format: "int" },
  {
    key: "available_date",
    label: "Available Date",
    position: 44,
    format: "date",
  },
  { key: "rent_info", label: "Rent Info", position: 45 },
  { key: "rental_type", label: "Rental Type", position: 46 },
  {
    key: "date_became_sold",
    label: "MLS Sold Date",
    position: 48,
    format: "date",
  },
  {
    key: "date_became_rented",
    label: "MLS Rented Date",
    position: 49,
    format: "date",
  },
  {
    key: "date_became_pending",
    label: "MLS Pending Date",
    position: 50,
    format: "date",
  },
  {
    key: "date_became_off_market",
    label: "MLS Off Market Date",
    position: 51,
    format: "date",
  },
  {
    key: "date_became_incomplete",
    label: "MLS Incomplete Date",
    position: 52,
    format: "date",
  },
  {
    key: "date_became_cancelled",
    label: "MLS Cancelled Date",
    position: 53,
    format: "date",
  },
  {
    key: "date_became_withdrawn",
    label: "MLS Withdrawn Date",
    position: 53.5,
    format: "date",
  },
  {
    key: "date_became_expired",
    label: "MLS Expiration Date",
    position: 54,
    format: "date",
  },
  { key: "cw_private_notes", label: "CW Private Notes", position: 64 },
  { key: "rcr_listing", label: "RCR Listing", position: 65 },
];

// Virtual $/SF columns. Numerators arrive from the API as STRINGS (Prisma
// Decimal serializes to JSON as "1769000"); sf_under_ac is a real number.
// Coerce first, then test — truthiness would let "0" through, and null/"" both
// coerce to 0 and would print a bogus $0.00.
const perSfValue = (numerator, sf) => {
  const n = Number(numerator);
  const d = Number(sf);
  if (!Number.isFinite(n) || !Number.isFinite(d) || n <= 0 || d <= 0) return null;
  return n / d;
};

// Returns null (never "") for the empty case: table.jsx rewrites "" to "-".
const formatPerSf = (val) => {
  if (val == null || !Number.isFinite(Number(val))) return null;
  return `$${Number(val).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const discountValue = (finalAmount, originalAmount) => {
  const finalNumber = Number(finalAmount);
  const originalNumber = Number(originalAmount);
  if (!Number.isFinite(finalNumber) || !Number.isFinite(originalNumber)) return null;
  return finalNumber - originalNumber;
};

const discountPercent = (finalAmount, originalAmount) => {
  const originalNumber = Number(originalAmount);
  if (!Number.isFinite(originalNumber) || originalNumber === 0) return null;
  const value = discountValue(finalAmount, originalAmount);
  return value == null ? null : (value / originalNumber) * 100;
};

// Discount columns compare the final price against the original asking price, so
// the sign carries the story: below asking (a drop) vs above asking (an increase).
// A thin arrow makes that readable at a glance; unchanged prices get no arrow.
// The arrow trails the number so the figures themselves line up down the column.
const discountArrow = (num) => {
  if (!Number.isFinite(num) || num === 0) return null;
  return (
    <span aria-hidden="true" className="ml-0.5 font-bold">
      {num < 0 ? "↓" : "↑"}
    </span>
  );
};

const formatSignedCurrency = (val) => {
  const num = Number(val);
  if (!Number.isFinite(num)) return null;
  const sign = num < 0 ? "-" : "";
  const text = `${sign}$${Math.abs(Math.round(num)).toLocaleString("en-US")}`;
  return (
    <span className="whitespace-nowrap">
      {text}
      {discountArrow(num)}
    </span>
  );
};

const formatSignedPercent = (val) => {
  const num = Number(val);
  if (!Number.isFinite(num)) return null;
  return (
    <span className="whitespace-nowrap">
      {`${num.toFixed(2)}%`}
      {discountArrow(num)}
    </span>
  );
};

// The matrix guide message reads as plain black text with only the actionable
// verb picked out in red. The wording is authored per community, so highlight
// "tap" wherever it falls instead of assuming a fixed sentence.
const highlightTapWords = (text) =>
  String(text || "")
    .split(/(\btaps?\b)/gi)
    .filter((part) => part !== "")
    .map((part, i) =>
      /^taps?$/i.test(part) ? (
        <span key={i} className="text-red-600">
          {part}
        </span>
      ) : (
        part
      )
    );

const Table = () => {
  const { pageHeader, isLoading } = usePageHeaderByKey("home");
  const { data, isLoading: siteLoading } = useSiteSettings();
  const [properties, setProperties] = useState([]); // Ensure properties starts as an array
  // The matrices render before /api/properties answers; this keeps each title
  // bar's property count hidden until then, instead of flashing "0 Properties".
  const [propertiesLoaded, setPropertiesLoaded] = useState(false);
  const [community, setCommunity] = useState(null);
  const [subdivisions, setSubdivisions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  // Each time-windowed matrix (SOLD, RENTED) owns its own selection; the controls
  // are rendered right above that matrix. Date range defaults to LAST 2 YEARS;
  // subdivision defaults to ALL ("").
  const [dateRangeFilters, setDateRangeFilters] = useState({
    SOLD: "LAST_1_YEAR",
    RENTED: "LAST_1_YEAR",
  });
  const [subdivisionFilters, setSubdivisionFilters] = useState({
    SOLD: "",
    RENTED: "",
  });
  const [activeHomeType, setActiveHomeType] = useState("ALL");
  const [activeSubdivision, setActiveSubdivision] = useState("");
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [matrixConfig, setMatrixConfig] = useState({
    matrixHomeTypeMenu: "NONE",
    matrixSubdivisionsMenu: "NONE",
    subdivisionNameForButtons: "SHORT_NAME",
    subdivisionNameForDropdown: "SHORT_NAME",
    matrixListingCategoryMenu: "BUTTONS",
    matrixDateRangeMenu: "NONE",
    matrixSoldSubdivisionMenu: "NONE",
    matrixSoldDateRangeMenu: "NONE",
    matrixRentedSubdivisionMenu: "NONE",
    matrixRentedDateRangeMenu: "NONE",
    promotion1Enabled: false,
    promotion1Text: null,
    promotion1Url: null,
    promotion2Enabled: false,
    promotion2Text: null,
    promotion2Url: null,
    promotion3Enabled: false,
    promotion3Text: null,
    promotion3Url: null,
    promotion4Enabled: false,
    promotion4Text: null,
    promotion4Url: null,
    promotion5Enabled: false,
    promotion5Text: null,
    promotion5Url: null,
  });
  const [allowedPropertyTypes, setAllowedPropertyTypes] = useState(null);
  const [matrixColumnConfigs, setMatrixColumnConfigs] = useState([]);
  const [matrixStatuses, setMatrixStatuses] = useState(DEFAULT_MATRIX_STATUSES);

  const saleScrollRef = useRef(null);
  const rentScrollRef = useRef(null);
  const soldScrollRef = useRef(null);
  const rentedScrollRef = useRef(null);

  // Each matrix's title bar doubles as the rest line for its column-header row,
  // which floats just beneath it (see useFloatingHeaderRow in table.jsx). Reading
  // the bar's own bottom edge keeps the two in step without re-deriving the site
  // header, filter bar and per-matrix control offsets a second time.
  const saleTitleRef = useRef(null);
  const rentTitleRef = useRef(null);
  const soldTitleRef = useRef(null);
  const rentedTitleRef = useRef(null);

  // The SOLD/RENTED matrices carry their own controls, which stick just under the
  // global filter bar so the bar cannot cover them on the way past. That needs the
  // bar's height, which changes as the filters wrap.
  //
  // A callback ref rather than useRef + useEffect: the bar only appears once the
  // matrix config has loaded, so an effect keyed on [] would measure a node that
  // does not exist yet and leave the offset at zero forever.
  const filterBarObserverRef = useRef(null);
  const setFilterBarRef = useCallback((node) => {
    if (filterBarObserverRef.current) {
      filterBarObserverRef.current.disconnect();
      filterBarObserverRef.current = null;
    }
    const publish = () =>
      document.documentElement.style.setProperty(
        "--matrix-filter-height",
        node ? `${Math.round(node.getBoundingClientRect().height)}px` : "0px",
      );
    publish();
    if (node && typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(publish);
      observer.observe(node);
      filterBarObserverRef.current = observer;
    }
  }, []);

  // Each matrix's red title bar floats too, and has to stop below that matrix's
  // own controls where it has any — those stick under the global bar, so a title
  // bar pinned at the same offset would hide behind them. Only SOLD / RENTED
  // carry controls and their height changes as the buttons wrap, so measure per
  // matrix instead of assuming one offset for all four.
  //
  // The ref callbacks are cached per status: handing React a fresh function each
  // render makes it detach and re-attach the node, and that null-then-node pair
  // would publish 0 and then the height again on every commit — a render loop.
  const [matrixControlsHeights, setMatrixControlsHeights] = useState({});
  const matrixControlsObservers = useRef({});
  const matrixControlsRefs = useRef({});
  const getMatrixControlsRef = useCallback((key) => {
    if (!matrixControlsRefs.current[key]) {
      matrixControlsRefs.current[key] = (node) => {
        const previous = matrixControlsObservers.current[key];
        if (previous) {
          previous.disconnect();
          delete matrixControlsObservers.current[key];
        }
        const publish = () => {
          const height = node
            ? Math.round(node.getBoundingClientRect().height)
            : 0;
          setMatrixControlsHeights((prev) =>
            prev[key] === height ? prev : { ...prev, [key]: height },
          );
        };
        publish();
        if (node && typeof ResizeObserver !== "undefined") {
          const observer = new ResizeObserver(publish);
          observer.observe(node);
          matrixControlsObservers.current[key] = observer;
        }
      };
    }
    return matrixControlsRefs.current[key];
  }, []);
  useEffect(
    () => () => {
      Object.values(matrixControlsObservers.current).forEach((observer) =>
        observer.disconnect(),
      );
      matrixControlsObservers.current = {};
    },
    [],
  );

  const scrollRefMap = { SALE: saleScrollRef, RENT: rentScrollRef, SOLD: soldScrollRef, RENTED: rentedScrollRef };
  const titleRefMap = { SALE: saleTitleRef, RENT: rentTitleRef, SOLD: soldTitleRef, RENTED: rentedTitleRef };

  useEffect(() => {
    axiosInstance
      .get("/api/properties") // Backend API endpoint
      .then((response) => {
        // Ensure data is an array before proceeding
        let arr = Array.isArray(response.data)
          ? response.data
          : [response.data];
        // Removed debug logs
        setProperties(arr);
        setPropertiesLoaded(true);
      })
      .catch((error) => console.error("Error fetching properties:", error));

    // Fetch community data with subdivisions
    axiosInstance
      .get("/api/community")
      .then((response) => {
        setCommunity(response.data);
        setSubdivisions(response.data.subdivisions || []);
      })
      .catch((error) => console.error("Error fetching community:", error));

    // Fetch matrix config and allowed types from admin config
    axiosInstance
      .get("/api/community/allowed-property-types")
      .then((res) => {
        setMatrixConfig({
          matrixHomeTypeMenu: res.data?.matrixHomeTypeMenu || "NONE",
          matrixSubdivisionsMenu: res.data?.matrixSubdivisionsMenu || "NONE",
          subdivisionNameForButtons:
            res.data?.subdivisionNameForButtons || "SHORT_NAME",
          subdivisionNameForDropdown:
            res.data?.subdivisionNameForDropdown || "SHORT_NAME",
          matrixListingCategoryMenu:
            res.data?.matrixListingCategoryMenu || "BUTTONS",
          matrixDateRangeMenu: res.data?.matrixDateRangeMenu || "NONE",
          // Per-matrix above-table filters; date-range menus fall back to the
          // deprecated single matrixDateRangeMenu for not-yet-resaved configs.
          matrixSoldSubdivisionMenu: res.data?.matrixSoldSubdivisionMenu || "NONE",
          matrixSoldDateRangeMenu:
            res.data?.matrixSoldDateRangeMenu ||
            res.data?.matrixDateRangeMenu ||
            "NONE",
          matrixRentedSubdivisionMenu:
            res.data?.matrixRentedSubdivisionMenu || "NONE",
          matrixRentedDateRangeMenu:
            res.data?.matrixRentedDateRangeMenu ||
            res.data?.matrixDateRangeMenu ||
            "NONE",
          matrixGuideText:
            res.data?.matrixGuideText ||
            "Tap on property for more details, tap on column headers to sort tables",
          matrixGuideTextForSale: res.data?.matrixGuideTextForSale ?? null,
          matrixGuideTextForRent: res.data?.matrixGuideTextForRent ?? null,
          matrixGuideTextSold: res.data?.matrixGuideTextSold ?? null,
          matrixGuideTextRented: res.data?.matrixGuideTextRented ?? null,
          mls_sale_list_link: res.data?.mls_sale_list_link ?? null,
          mls_rental_list_link: res.data?.mls_rental_list_link ?? null,
          mls_sold_list_link: res.data?.mls_sold_list_link ?? null,
          mls_rented_list_link: res.data?.mls_rented_list_link ?? null,
          promotion1Enabled: Boolean(res.data?.promotion1Enabled),
          promotion1Text: res.data?.promotion1Text ?? null,
          promotion1Url: res.data?.promotion1Url ?? null,
          promotion2Enabled: Boolean(res.data?.promotion2Enabled),
          promotion2Text: res.data?.promotion2Text ?? null,
          promotion2Url: res.data?.promotion2Url ?? null,
          promotion3Enabled: Boolean(res.data?.promotion3Enabled),
          promotion3Text: res.data?.promotion3Text ?? null,
          promotion3Url: res.data?.promotion3Url ?? null,
          promotion4Enabled: Boolean(res.data?.promotion4Enabled),
          promotion4Text: res.data?.promotion4Text ?? null,
          promotion4Url: res.data?.promotion4Url ?? null,
          promotion5Enabled: Boolean(res.data?.promotion5Enabled),
          promotion5Text: res.data?.promotion5Text ?? null,
          promotion5Url: res.data?.promotion5Url ?? null,
        });
        // Keyed FOR_SALE / FOR_RENT on the wire; map to the internal matrix keys.
        if (res.data?.matrixStatuses) {
          const mapped = { ...DEFAULT_MATRIX_STATUSES };
          for (const [configKey, internalKey] of Object.entries(MATRIX_KEY_BY_CONFIG_KEY)) {
            const list = res.data.matrixStatuses[configKey];
            if (Array.isArray(list)) mapped[internalKey] = list.map((v) => String(v).toUpperCase());
          }
          setMatrixStatuses(mapped);
        }
        if (res.data?.allowedTypes?.length) {
          setAllowedPropertyTypes(
            new Set(res.data.allowedTypes.map((t) => t.toUpperCase())),
          );
        }
      })
      .catch(() =>
        setMatrixConfig((prev) => ({ ...prev, matrixHomeTypeMenu: "NONE" })),
      );

    // Fetch matrix column config (which columns to show per matrix type)
    axiosInstance
      .get("/api/community/matrix-columns")
      .then((res) =>
        setMatrixColumnConfigs(Array.isArray(res.data) ? res.data : []),
      )
      .catch(() => setMatrixColumnConfigs([]));
  }, []);

  const handleSearch = (e) => {
    setSearchQuery(e.toLowerCase());
  };

  const handleFilterChange = (value) => {
    setFilterStatus(value);
  };

  const normalizeHomeType = (homeType) => {
    if (!homeType || String(homeType).trim() === "") return "SFH";
    const t = String(homeType).trim().toUpperCase();
    if (t === "COACHHOME" || t === "COACH HOME") return "CoachHome";
    return String(homeType).trim();
  };

  // Map API matrix_type to internal status title
  const statusToMatrixType = {
    Sale: "FOR_SALE",
    Rent: "FOR_RENT",
    Sold: "SOLD",
    Rented: "RENTED",
  };
  // field_key -> dataIndex (or computed key for display)
  const FIELD_KEY_TO_DATA_INDEX = {
    price_or_rent: "price_or_rent",
    date_became_sold: "date_became_sold",
    unit_number: "unit_number",
    bedrooms: "bedrooms",
    bathrooms: "bathrooms",
    half_bathrooms: "half_bathrooms",
    sf_under_ac: "sf_under_ac",
    water_view: "_computed_water_view",
    furniture: "_computed_furniture",
    available_date: "available_date",
    rent_info: "_computed_rental_prices",
    rental_type: "rental_type",
    date_became_rented: "date_became_rented",
    original_asking_price: "original_asking_price",
    original_asking_rent: "original_asking_rent",
    previous_sold_price: "previous_sold_price",
    asking_price_per_sf: "_computed_asking_price_per_sf",
    prev_sold_price_per_sf: "_computed_prev_sold_price_per_sf",
    asking_rent_per_sf: "_computed_asking_rent_per_sf",
    sold_price_per_sf: "_computed_sold_price_per_sf",
    rented_price_per_sf: "_computed_rented_price_per_sf",
    sold_dollar_discount: "_computed_sold_dollar_discount",
    sold_percent_discount: "_computed_sold_percent_discount",
    rented_dollar_discount: "_computed_rented_dollar_discount",
    rented_percent_discount: "_computed_rented_percent_discount",
    previous_sold_date: "previous_sold_date",
    dom: "dom",
    real_estate_tax: "real_estate_tax",
    county_assessed_value: "county_assessed_value",
    short_address: "_computed_address",
    very_short_address: "very_short_address",
    subdivision_short: "_subdivision",
    garage: "_computed_garage",
    private_pool: "_computed_pool",
    home_type: "home_type",
  };

  /**
   * Which matrices a listing belongs in, as an array — the admin can put one
   * status on both matrices of a side (say OFF MARKET on FOR SALE and SOLD), and
   * a listing that qualifies for both should appear in both rather than silently
   * picking one.
   *
   * Side is decided by listing type, so the SOLD matrix cannot fill up with
   * rentals. SOLD and RENTED are the exception: they name their own side, because
   * a listing that closed as a sale is a sale whatever its listing type column
   * says. Letting listing type win there would silently drop a mis-typed listing
   * off the site instead of showing it, which is the worse of the two failures.
   * Anything else with no listing type stays hidden, as it was.
   */
  const getMatrixStatuses = (property) => {
    const status = (
      property.cw_listing_status ||
      property.status ||
      ""
    ).toUpperCase();
    if (!status) return [];
    // Legacy `status` column: "Sale" / "Rent" name the matrix outright.
    if (status === "SALE") return ["SALE"];
    if (status === "RENT") return ["RENT"];

    const listingType = (property.listing_type || "").toLowerCase();
    const side =
      status === "SOLD"
        ? "sale"
        : status === "RENTED"
          ? "rental"
          : listingType.includes("rental")
            ? "rental"
            : listingType.includes("sale")
              ? "sale"
              : null;
    if (!side) return [];

    return MATRIX_ORDER.filter(
      (key) =>
        MATRIX_LISTING_SIDE[key] === side &&
        (matrixStatuses[key] || []).includes(status),
    );
  };

  const extractSubdivision = (property) => {
    // Use the subdivision relation object from the API (has id, shortName, fullName, homeType)
    if (property?.subdivision && typeof property.subdivision === "object") {
      return (
        property.subdivision.shortName || property.subdivision.fullName || null
      );
    }
    // Fallback: use subdivision_id to look up from community subdivisions
    if (property?.subdivision_id) {
      const sub = subdivisions.find((s) => s.id === property.subdivision_id);
      if (sub) return sub.shortName;
    }
    return null;
  };

  const propertiesWithDefaults = useMemo(
    () =>
      properties.map((property) => ({
        ...property,
        home_type: normalizeHomeType(property.home_type),
        _subdivision: extractSubdivision(property),
      })),
    [properties, subdivisions],
  );

  const uniqueHomeTypes = useMemo(
    () =>
      Array.from(
        new Set(
          propertiesWithDefaults
            .map((property) => normalizeHomeType(property.home_type))
            .filter(Boolean),
        ),
      ),
    [propertiesWithDefaults],
  );

  const uniqueSubdivisions = useMemo(
    () =>
      Array.from(
        new Set(
          propertiesWithDefaults
            .map((property) => property._subdivision)
            .filter(Boolean),
        ),
      ),
    [propertiesWithDefaults],
  );

  // Determine if community is uniform (all same home type) or non-uniform (mixed)
  const isUniformCommunity = useMemo(() => {
    return uniqueHomeTypes.length === 1;
  }, [uniqueHomeTypes]);

  // Determine if community has subdivisions
  const hasSubdivisions = useMemo(() => {
    return subdivisions && subdivisions.length > 0;
  }, [subdivisions]);

  const STANDARD_HOME_TYPES = [
    { label: "SFH", value: "SFH" },
    { label: "CONDO", value: "Condo" },
    { label: "TH", value: "TH" },
    { label: "VILLA", value: "Villa" },
    { label: "COACH HOME", value: "CoachHome" },
  ];

  const homeTypeDropdownOptions = useMemo(() => {
    const fromSubs = (subdivisions || [])
      .map((s) => (s.homeType ? normalizeHomeType(s.homeType) : null))
      .filter(Boolean);
    const fromProps = uniqueHomeTypes.map((t) =>
      t === "COACHHOME" || t === "COACH HOME" ? "CoachHome" : t,
    );
    // When community has subdivisions, use only those — don't add types from legacy property data
    const existing =
      hasSubdivisions && fromSubs.length > 0
        ? fromSubs
        : [...new Set([...fromSubs, ...fromProps])];
    let typeOptions = STANDARD_HOME_TYPES.filter((opt) =>
      existing.some((e) => String(e).toUpperCase() === opt.value.toUpperCase()),
    );
    // Filter by admin-configured allowed property types
    if (allowedPropertyTypes) {
      typeOptions = typeOptions.filter((opt) =>
        allowedPropertyTypes.has(opt.value.toUpperCase()),
      );
    }
    const base = [{ label: "ALL", value: "ALL" }, ...typeOptions];
    return base;
  }, [uniqueHomeTypes, subdivisions, hasSubdivisions, allowedPropertyTypes]);

  const getSubdivisionLabel = (s, forButtons) => {
    const key = forButtons
      ? matrixConfig.subdivisionNameForButtons
      : matrixConfig.subdivisionNameForDropdown;
    if (key === "FULL_NAME") return s.fullName || s.shortName || "";
    if (key === "VERY_SHORT_NAME") return s.shortestName || s.shortName || "";
    return s.shortName || s.fullName || "";
  };

  const subdivisionDropdownOptions = useMemo(() => {
    const opts = (subdivisions || [])
      .filter((s) => {
        if (!allowedPropertyTypes || !s.homeType) return true;
        return allowedPropertyTypes.has(s.homeType.toUpperCase());
      })
      .map((s) => ({
        label: getSubdivisionLabel(
          s,
          matrixConfig.matrixSubdivisionsMenu === "BUTTONS",
        ),
        value: s.shortName,
      }));
    return opts.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
  }, [
    subdivisions,
    allowedPropertyTypes,
    matrixConfig.matrixSubdivisionsMenu,
    matrixConfig.subdivisionNameForButtons,
    matrixConfig.subdivisionNameForDropdown,
  ]);

  // Single home type/subdivision: hide filters per design rules (e.g. Toscana)
  const hasSingleHomeType = useMemo(() => {
    const count = homeTypeDropdownOptions.filter(
      (o) => o.value !== "ALL",
    ).length;
    return count <= 1;
  }, [homeTypeDropdownOptions]);

  const hasSingleSubdivision = useMemo(() => {
    return (subdivisions?.length ?? 0) <= 1;
  }, [subdivisions]);

  // Subdivision is offered in two places: the global filter bar (narrows every
  // matrix) and a per-matrix selector above SOLD / RENTED (narrows just that
  // one). They render identical rows, and both are sticky, so showing them
  // together stacks the same buttons twice as a matrix scrolls under the bar.
  //
  // Exactly one of them is on screen at a time. The global bar owns the control
  // by default; it steps aside only when the category filter has already
  // narrowed the page to SOLD or RENTED, and each matrix then shows its own.
  const topSubdivisionHidden =
    (filterStatus === "SOLD" || filterStatus === "RENTED") &&
    activeHomeType !== "__SUBDIVISION__";

  const topSubdivisionVisible =
    hasSubdivisions &&
    !hasSingleSubdivision &&
    (matrixConfig?.matrixSubdivisionsMenu || "NONE") !== "NONE" &&
    !topSubdivisionHidden &&
    subdivisionDropdownOptions.length > 0;

  const statusDropdownOptions = [
    { label: "ALL", value: "ALL" },
    { label: "FOR SALE", value: "SALE" },
    { label: "FOR RENT", value: "RENT" },
    { label: "SOLD", value: "SOLD" },
    { label: "RENTED", value: "RENTED" },
  ];

  const filteredProperties = useMemo(() => {
    let filteredByHomeType = propertiesWithDefaults.filter((property) => {
      if (activeHomeType === "ALL") return true;
      if (activeHomeType === "__SUBDIVISION__") {
        if (!activeSubdivision) return false;
        return property._subdivision === activeSubdivision;
      }
      return (
        normalizeHomeType(property.home_type).toUpperCase() ===
        activeHomeType.toUpperCase()
      );
    });

    return filteredByHomeType.filter((property) => {
      const matchesSearch =
        searchQuery === "" ||
        Object.values(property).some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(searchQuery),
        );
      const propertyMatrices = getMatrixStatuses(property);
      const matchesStatus =
        filterStatus === "ALL" || propertyMatrices.includes(filterStatus);
      return matchesSearch && matchesStatus;
    });
  }, [
    activeHomeType,
    activeSubdivision,
    filterStatus,
    matrixStatuses,
    propertiesWithDefaults,
    searchQuery,
  ]);

  // Community-wide SOLD / RENTED counts for the CURRENT YEAR (Jan 1 → now), independent of
  // the home-type/subdivision selection. Used to decide whether to offer the CURRENT YEAR
  // time-range option above each matrix (SOLD needs ≥ 5, RENTED needs ≥ 3).
  const { currentYearSold, currentYearRented } = useMemo(() => {
    // Jan 1 of the current calendar year at UTC midnight — must match dateRangeCutoff, or a
    // listing dated Jan 1 counts toward one and not the other.
    const start = new Date(Date.UTC(new Date().getFullYear(), 0, 1));
    const inCurrentYear = (raw) => {
      if (!raw) return false;
      const d = new Date(raw);
      return !isNaN(d.getTime()) && d >= start;
    };
    let sold = 0;
    let rented = 0;
    for (const p of propertiesWithDefaults) {
      const s = getMatrixStatuses(p);
      if (s.includes("SOLD") && inCurrentYear(p.date_became_sold)) sold++;
      if (s.includes("RENTED") && inCurrentYear(p.date_became_rented)) rented++;
    }
    return { currentYearSold: sold, currentYearRented: rented };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertiesWithDefaults, matrixStatuses]);

  const showCurrentYearSold = currentYearSold >= 5;
  const showCurrentYearRented = currentYearRented >= 3;

  // If the CURRENT YEAR option becomes hidden (count too low) while it's still the active
  // selection, fall back to the default so a matrix can't get stuck filtering on a hidden option.
  useEffect(() => {
    setDateRangeFilters((prev) => ({
      ...prev,
      SOLD:
        !showCurrentYearSold && prev.SOLD === "THIS_YEAR" ? "LAST_1_YEAR" : prev.SOLD,
      RENTED:
        !showCurrentYearRented && prev.RENTED === "THIS_YEAR"
          ? "LAST_1_YEAR"
          : prev.RENTED,
    }));
  }, [showCurrentYearSold, showCurrentYearRented]);

  const openDetailModal = (property) => {
    // Modal removed — navigation is now handled per-cell
  };

  const closeDetailModal = () => {
    // Modal removed
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  };

  function parseJsonFields(obj) {
    const parsedObj = {};

    for (const key in obj) {
      if (
        typeof obj[key] === "string" &&
        obj[key].startsWith("[") &&
        obj[key].endsWith("]")
      ) {
        try {
          parsedObj[key] = JSON.parse(obj[key]); // Convert JSON string to an array
        } catch (error) {
          console.error(`Error parsing ${key}:`, error);
          parsedObj[key] = obj[key];
        }
      } else {
        parsedObj[key] = obj[key];
      }
    }

    return parsedObj;
  }

  const formatArrayField = (value) => {
    if (Array.isArray(value)) return value.join(", "); // Convert array to a string with spaces
    if (
      typeof value === "string" &&
      value.startsWith("[") &&
      value.endsWith("]")
    ) {
      try {
        return JSON.parse(value).join(", "); // Parse and format if it's a JSON string
      } catch {
        return value; // Return original if parsing fails
      }
    }
    return value;
  };

  // Helper function to format address (Street Address or Unit number)
  const formatAddress = (property) => {
    const clean = (v) => {
      if (v === null || v === undefined) return null;
      const s = String(v).trim();
      if (s === "" || s.toLowerCase() === "none" || s.toUpperCase() === "N/A")
        return null;
      return s;
    };

    const streetNumber = clean(property.street_number);
    const compassDirection = clean(property.compass_direction);
    const streetName = clean(property.street_name);
    const postStreetDirection = clean(property.post_street_direction);
    const unitNumber = clean(property.unit_number);

    // Build street address
    const streetParts = [streetNumber];
    if (compassDirection) streetParts.push(compassDirection);
    if (streetName) streetParts.push(streetName);
    if (postStreetDirection) streetParts.push(postStreetDirection);
    const streetAddress = streetParts.filter(Boolean).join(" ");

    // Prioritize street address, fall back to unit number if street address not available
    if (streetAddress && streetAddress.trim() !== "") {
      return streetAddress;
    }
    if (unitNumber) {
      return unitNumber;
    }
    return "N/A";
  };

  // Helper function to determine pool status
  const getPoolStatus = (property) => {
    const privatePool =
      property.private_pool === true ||
      property.private_pool === "YES" ||
      property.private_pool === "Yes";

    return privatePool ? "Yes" : "No";
  };

  // Helper function to determine garage status
  const getGarageStatus = (property) => {
    const coveredGarage = property.covered_garage;

    // Check covered_garage numeric field
    if (coveredGarage && coveredGarage !== "0" && coveredGarage !== "")
      return coveredGarage;

    return "0";
  };

  // Helper function to determine water view status
  const getWaterViewStatus = (property) => {
    const normalized = String(property.water_view || "").trim().toUpperCase();

    if (normalized === "YES" || normalized === "NO") return normalized;
    if (normalized === "UKN" || normalized === "UNK" || normalized === "UNKNOWN") {
      return "UKN";
    }

    return "UKN";
  };

  const renderTable = (
    status,
    dataset,
    subdivisionName = null,
    options = {},
  ) => {
    const { allOfTypeLabel = null, showSubdivisionColumn = false } = options;
    // Normalize status for filtering and column logic
    const statusUpper = (status || "").toUpperCase();
    const statusTitle =
      statusUpper === "SALE"
        ? "Sale"
        : statusUpper === "RENT"
          ? "Rent"
          : statusUpper === "SOLD"
            ? "Sold"
            : statusUpper === "RENTED"
              ? "Rented"
              : statusUpper === "ALL"
                ? "ALL"
                : status;

    // Use uppercase for filtering
    let listingStatusFilter = statusUpper === "ALL" ? null : statusUpper;

    // Determine table header based on subdivisions
    const getTableHeader = () => {
      if (allOfTypeLabel) {
        if (status.toLowerCase() === "sale")
          return `FOR SALE (${allOfTypeLabel})`;
        if (status.toLowerCase() === "rent")
          return `FOR RENT (${allOfTypeLabel})`;
        if (status.toLowerCase() === "sold") return `SOLD (${allOfTypeLabel})`;
        if (status.toLowerCase() === "rented")
          return `RENTED (${allOfTypeLabel})`;
      }
      if (subdivisionName) {
        const subdivision = subdivisions.find(
          (s) =>
            s.fullName === subdivisionName || s.shortName === subdivisionName,
        );
        const displayName = subdivision?.shortName || subdivisionName;
        if (status.toLowerCase() === "sale") return `FOR SALE (${displayName})`;
        if (status.toLowerCase() === "sold") return `SOLD (${displayName})`;
        if (status.toLowerCase() === "rented") return `RENTED (${displayName})`;
        if (status.toLowerCase() === "rent") return `FOR RENT (${displayName})`;
      }
      const displayName =
        community?.shortName || community?.name || "Community";
      if (status.toLowerCase() === "sale") return `FOR SALE (${displayName})`;
      if (status.toLowerCase() === "sold") return `SOLD (${displayName})`;
      if (status.toLowerCase() === "rented") return `RENTED (${displayName})`;
      if (status.toLowerCase() === "rent") return `FOR RENT (${displayName})`;
      return displayName;
    };

    const statusFiltered = dataset.filter((property) => {
      if (!listingStatusFilter) return true;
      return getMatrixStatuses(property).includes(listingStatusFilter);
    });

    // Time-range filter (ALL / THIS YEAR / LAST 1 YEAR / LAST 2 YEARS) — applies only to
    // the SOLD and RENTED matrices, windowing on the date the listing became sold/rented.
    // Each matrix owns its own selection, rendered right above that matrix.
    const dateRangeField = DATE_RANGE_FIELD_BY_STATUS[statusUpper];
    const dateRangeMenuKey = DATE_RANGE_MENU_KEY_BY_STATUS[statusUpper];
    const dateRangeMenu =
      (dateRangeMenuKey && matrixConfig?.[dateRangeMenuKey]) || "NONE";
    const showDateRange = dateRangeMenu !== "NONE" && Boolean(dateRangeField);
    const activeDateRange = dateRangeFilters[statusUpper] || "ALL";
    const setThisDateRange = (val) =>
      setDateRangeFilters((prev) => ({ ...prev, [statusUpper]: val }));
    // Cutoff (inclusive lower bound) per option. THIS YEAR is an absolute calendar boundary
    // (Jan 1 of the current year); LAST 1/2 YEARS are rolling windows back from today. ALL = no window.
    // Anchored to UTC midnight: the property dates being compared are calendar days stored
    // at UTC midnight, so a local-midnight cutoff would sit hours ahead of them and drop
    // listings whose date falls exactly on the boundary.
    const dateRangeCutoff = (() => {
      if (activeDateRange === "ALL") return null;
      const today = new Date();
      const cutoff = new Date(
        Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
      );
      if (activeDateRange === "THIS_YEAR") {
        cutoff.setUTCMonth(0, 1); // Jan 1 of the current calendar year
      } else if (activeDateRange === "LAST_1_YEAR") {
        cutoff.setUTCFullYear(cutoff.getUTCFullYear() - 1);
      } else if (activeDateRange === "LAST_2_YEARS") {
        cutoff.setUTCFullYear(cutoff.getUTCFullYear() - 2);
      } else {
        return null;
      }
      return cutoff;
    })();
    const rangeFiltered =
      dateRangeField && dateRangeCutoff
        ? statusFiltered.filter((property) => {
            const raw = property[dateRangeField];
            if (!raw) return false;
            const soldOrRentedDate = new Date(raw);
            if (isNaN(soldOrRentedDate.getTime())) return false;
            return soldOrRentedDate >= dateRangeCutoff;
          })
        : statusFiltered;

    // Per-matrix subdivision selector (SOLD / RENTED only), governed by its own admin
    // menu (matrixSold/RentedSubdivisionMenu). Narrows this matrix only. "" = ALL.
    const subdivisionMenuKey = SUBDIVISION_MENU_KEY_BY_STATUS[statusUpper];
    const subdivisionMenu =
      (subdivisionMenuKey && matrixConfig?.[subdivisionMenuKey]) || "NONE";
    const subdivisionFilterApplies = Boolean(dateRangeField);
    const showMatrixSubdivision =
      subdivisionFilterApplies &&
      hasSubdivisions &&
      !hasSingleSubdivision &&
      // When a specific subdivision is chosen at the top, the matrix is already scoped to
      // it (header shows its name) — suppress the per-matrix selector to avoid conflict.
      activeHomeType !== "__SUBDIVISION__" &&
      // ...and never alongside the global subdivision row, which offers the very
      // same buttons. See topSubdivisionVisible.
      !topSubdivisionVisible &&
      subdivisionMenu !== "NONE" &&
      subdivisionDropdownOptions.length > 0;
    const activeMatrixSubdivision = subdivisionFilterApplies
      ? subdivisionFilters[statusUpper] || ""
      : "";
    const setThisMatrixSubdivision = (val) =>
      setSubdivisionFilters((prev) => ({ ...prev, [statusUpper]: val }));
    const subdivisionFiltered = activeMatrixSubdivision
      ? rangeFiltered.filter((p) => p._subdivision === activeMatrixSubdivision)
      : rangeFiltered;

    const selectedMatrixSubdivision = activeMatrixSubdivision
      ? subdivisions.find((s) => s.shortName === activeMatrixSubdivision)
      : null;
    const selectedTopSubdivision = subdivisionName
      ? subdivisions.find(
          (s) =>
            s.fullName === subdivisionName || s.shortName === subdivisionName,
        )
      : null;
    const emptySubdivisionName =
      selectedMatrixSubdivision?.shortName ||
      selectedTopSubdivision?.shortName ||
      null;

    // Get empty state message based on status and subdivisions
    const getEmptyMessage = () => {
      const scope = emptySubdivisionName
        ? ` in ${emptySubdivisionName}`
        : " in the community";
      if (status.toLowerCase() === "sale") {
        return <>No homes currently <strong>for sale</strong>{scope}.</>;
      } else if (status.toLowerCase() === "rent") {
        return <>No homes currently <strong>for rent</strong>{scope}.</>;
      } else if (status.toLowerCase() === "sold") {
        return <>No homes currently <strong>sold</strong>{scope}.</>;
      } else if (status.toLowerCase() === "rented") {
        return <>No homes currently <strong>rented</strong>{scope}.</>;
      }
      return <>No properties available.</>;
    };

    const shortAddress = (p) =>
      p.short_address && String(p.short_address).trim()
        ? String(p.short_address).trim()
        : formatAddress(p);

    const RENTAL_PRICE_FIELDS = [
      { key: "furnished_annual",       label: "Furnished Annual" },
      { key: "furnished_seasonal",     label: "Furnished Seasonal" },
      { key: "furnished_off_season",   label: "Furnished Off Season" },
      { key: "unfurnished_annual",     label: "Unfurnished Annual" },
      { key: "unfurnished_seasonal",   label: "Unfurnished Seasonal" },
      { key: "unfurnished_off_season", label: "Unfurnished Off Season" },
    ];

    const computeRentalPrices = (property) => {
      const parts = RENTAL_PRICE_FIELDS
        .filter(({ key }) => {
          const v = property[key];
          return v != null && v !== "" && Number(v) !== 0;
        })
        .map(({ key, label }) => `${label}: $${Number(property[key]).toLocaleString("en-US")}`);
      return parts.length > 0 ? parts.join("; ") : "Unknown";
    };

    const buildRequestPropertyAddress = (property) => {
      const streetParts = [
        property.street_number,
        property.compass_direction,
        property.street_name,
        property.post_street_direction,
      ]
        .map((part) => String(part || "").trim())
        .filter(Boolean);
      const streetAddress = streetParts.join(" ");
      const unit = String(property.unit_number || "").trim();
      const cityStateZip = [
        String(property.city || "").trim(),
        [property.state, property.zip_code]
          .map((part) => String(part || "").trim())
          .filter(Boolean)
          .join(" "),
      ]
        .filter(Boolean)
        .join(", ");
      const fullAddress = [
        streetAddress,
        unit ? `Apt ${unit}` : "",
        cityStateZip,
      ]
        .filter(Boolean)
        .join(", ");
      return (
        fullAddress ||
        property.short_address ||
        property.very_short_address ||
        formatAddress(property)
      );
    };

    const openRequestInfoForm = (property) => {
      const address = buildRequestPropertyAddress(property);
      window.location.href = `/contact?property=${encodeURIComponent(address)}`;
    };

    const enrichedData = subdivisionFiltered.map((property) => ({
      ...property,
      _computed_address: shortAddress(property),
      _computed_pool: getPoolStatus(property),
      _computed_garage: getGarageStatus(property),
      _computed_water_view: getWaterViewStatus(property),
      _computed_furniture: property.furniture ?? "N/A",
      _computed_rental_prices: computeRentalPrices(property),
      _computed_asking_price_per_sf: perSfValue(property.original_asking_price, property.sf_under_ac),
      _computed_prev_sold_price_per_sf: perSfValue(property.previous_sold_price, property.sf_under_ac),
      _computed_asking_rent_per_sf: perSfValue(property.original_asking_rent, property.sf_under_ac),
      _computed_sold_price_per_sf: perSfValue(property.mls_sold_price, property.sf_under_ac),
      _computed_rented_price_per_sf: perSfValue(property.rented_price, property.sf_under_ac),
      _computed_sold_dollar_discount: discountValue(property.mls_sold_price, property.original_asking_price),
      _computed_sold_percent_discount: discountPercent(property.mls_sold_price, property.original_asking_price),
      _computed_rented_dollar_discount: discountValue(property.rented_price, property.original_asking_rent),
      _computed_rented_percent_discount: discountPercent(property.rented_price, property.original_asking_rent),
    }));

    const normalizeUrl = (val) => {
      if (val === null || val === undefined) return null;
      const s = String(val).trim();
      if (!s) return null;
      if (s.toLowerCase() === "blank") return null;
      if (/^https?:\/\//i.test(s)) return s;
      // Some imported data omits the scheme (e.g. "www.example.com/..." or "example.com/...")
      if (/^www\./i.test(s)) return `https://${s}`;
      if (/^[a-z0-9.-]+\.[a-z]{2,}(\/|$)/i.test(s)) return `https://${s}`;
      return null;
    };

    const handleCellClick = (property, columnKey) => {
      // Price columns -> property details URL (new tab)
      const priceKeys = new Set(["price_or_rent", "sold_price", "mls_sold_price", "rented_price"]);
      if (priceKeys.has(columnKey)) {
        const url = normalizeUrl(property.mls_link || property.property_details_url);
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        }
        return;
      }
      // Unit number / address columns -> county records URL (new tab)
      const addressKeys = new Set(["unit_number", "_computed_address", "short_address", "very_short_address"]);
      if (addressKeys.has(columnKey)) {
        const url = normalizeUrl(property.county_property_link);
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        }
        return;
      }
    };

    const linkableColumns = new Set([
      "price_or_rent", "sold_price", "mls_sold_price", "rented_price",
      "unit_number", "_computed_address", "short_address", "very_short_address",
    ]);

    // Determine if dataset contains only Condos
    const isCondoOnly =
      enrichedData.length > 0 &&
      enrichedData.every(
        (property) =>
          normalizeHomeType(property.home_type).toUpperCase() === "CONDO",
      );

    // Determine if dataset contains multiple home types
    const uniqueHomeTypesInData =
      enrichedData.length > 0
        ? Array.from(
          new Set(
            enrichedData.map((p) =>
              normalizeHomeType(p.home_type).toUpperCase(),
            ),
          ),
        )
        : [];
    const hasMultipleHomeTypes = uniqueHomeTypesInData.length >= 2;

    const matrixType = statusToMatrixType[statusTitle];
    const configList = (matrixColumnConfigs || [])
      .filter(
        (c) =>
          c.matrix_type === matrixType && (c.property_type || "ALL") === "ALL",
      )
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    const formatColumnTitle = (header) => {
      const text = (header || "").toUpperCase().trim();
      const NO_SPLIT_HEADERS = new Set([
        "RENTAL TYPE",
        "ASKING RENT DETAIL",
        "CURRENT ASKING RENT DETAIL",
      ]);
      if (NO_SPLIT_HEADERS.has(text)) return text;
      const parts = text.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (
          <span className="leading-tight block text-center">
            {parts.map((part, i) => (
              <span key={`${part}-${i}`} className="block">{part}</span>
            ))}
          </span>
        );
      }
      return text || "";
    };

    // "ASKING PER SF" -> ASKING / PER SF, "PREV SOLD PER SF" -> PREV / SOLD / PER SF.
    // The trailing pair ("PER SF", "OFF SEA") shares the last line so the unit
    // stays readable; everything before it stacks one word per line.
    const stackedHeaderLines = (header) => {
      const parts = (header || "").toUpperCase().trim().split(/\s+/).filter(Boolean);
      if (parts.length <= 2) return parts;
      return [...parts.slice(0, -2), parts.slice(-2).join(" ")];
    };

    const DOLLAR_VALUE_FIELDS = new Set([
      "price_or_rent",
      "sold_price",
      "mls_sold_price",
      "rented_price",
      "original_asking_price",
      "original_asking_rent",
      "previous_sold_price",
      "real_estate_tax",
      "county_assessed_value",
      "furnished_annual",
      "furnished_seasonal",
      "furnished_off_season",
      "unfurnished_annual",
      "unfurnished_seasonal",
      "unfurnished_off_season",
    ]);

    // Deliberately NOT part of DOLLAR_VALUE_FIELDS: that renderer uses a bare
    // toLocaleString (no minimumFractionDigits) and would print "$742.5".
    const PER_SF_FIELDS = new Set([
      "asking_price_per_sf",
      "prev_sold_price_per_sf",
      "asking_rent_per_sf",
      "sold_price_per_sf",
      "rented_price_per_sf",
    ]);

    const DOLLAR_DISCOUNT_FIELDS = new Set([
      "sold_dollar_discount",
      "rented_dollar_discount",
    ]);

    const PERCENT_DISCOUNT_FIELDS = new Set([
      "sold_percent_discount",
      "rented_percent_discount",
    ]);

    const buildColumnsFromConfig = () => {
      const base = [];
      configList.forEach((c, idx) => {
        const dataIndex = FIELD_KEY_TO_DATA_INDEX[c.field_key] || c.field_key;
        const mappings =
          c.value_mappings && typeof c.value_mappings === "object"
            ? c.value_mappings
            : null;
        const FIELD_HEADER_OVERRIDES = {
          mls_listing_status: "MLS Listing Status",
          county_assessed_value: "County Assessed Value",
          mls_sold_price: "Sold Price",
          furnished_seasonal: "Furn Season",
          unfurnished_seasonal: "Unfurn Season",
          furnished_off_season: "Furn Off Sea",
          unfurnished_off_season: "Unfurn Off Sea",
        };
        const columnHeader = FIELD_HEADER_OVERRIDES[c.field_key] || c.column_header || c.field_key || "";
        const OFF_SEA_FIELDS = new Set(["furnished_off_season", "unfurnished_off_season"]);
        let title;
        if (OFF_SEA_FIELDS.has(c.field_key) || PER_SF_FIELDS.has(c.field_key)) {
          title = (
            <span className="leading-tight block text-center">
              {stackedHeaderLines(columnHeader).map((line, i) => (
                <span key={`${line}-${i}`} className="block">{line}</span>
              ))}
            </span>
          );
        } else {
          title = formatColumnTitle(columnHeader);
        }
        base.push({
          title,
          dataIndex,
          key: `${c.field_key}-${idx}`,
          type: "string",
          position: base.length,
          ...(c.field_key === "rent_info" && { align: "left", nowrap: true }),
          ...(c.field_key === "rental_term" && { nowrap: true }),
          ...(c.field_key === "rental_type" && { nowrap: true }),
          ...(mappings && {
            render: (val) => {
              if (val == null || val === "") return "";
              const str = String(val).trim();
              return (
                mappings[str] ??
                mappings[str.toUpperCase()] ??
                str.toUpperCase()
              );
            },
          }),
          ...(DOLLAR_VALUE_FIELDS.has(c.field_key) && {
            render: (val) => {
              if (val == null || val === "") return "";
              return `$${Number(val).toLocaleString("en-US")}`;
            },
          }),
          // Must remain the LAST render spread: all three write the same `render`
          // key into this literal and the last one wins.
          ...(PER_SF_FIELDS.has(c.field_key) && {
            render: (val) => formatPerSf(val),
          }),
          ...(DOLLAR_DISCOUNT_FIELDS.has(c.field_key) && {
            align: "center",
            render: (val) => formatSignedCurrency(val),
          }),
          ...(PERCENT_DISCOUNT_FIELDS.has(c.field_key) && {
            align: "center",
            render: (val) => formatSignedPercent(val),
          }),
        });
      });
      return base;
    };

    let columns =
      configList.length > 0
        ? buildColumnsFromConfig()
        : [
          ...(statusTitle === "Rented"
            ? [
              {
                title: "Rented rent",
                key: "price_or_rent",
                dataIndex: "price_or_rent",
                type: "number",
                position: 1,
              },
            ]
            : statusTitle === "Sale"
              ? [
                {
                  title: "Asking price",
                  key: "price_or_rent",
                  dataIndex: "price_or_rent",
                  type: "number",
                  position: 1,
                },
              ]
              : statusTitle === "Rent"
                ? [
                  {
                    title: "Asking rent",
                    key: "price_or_rent",
                    dataIndex: "price_or_rent",
                    type: "number",
                    position: 1,
                  },
                ]
                : statusTitle === "Sold"
                  ? [
                    {
                      title: "Sold price",
                      key: "price_or_rent",
                      dataIndex: "price_or_rent",
                      type: "number",
                      position: 1,
                    },
                  ]
                  : []),

          ...(statusTitle === "Rented"
            ? [
              {
                title: "Rented date",
                dataIndex: "date_became_rented",
                key: "date_became_rented",
                type: "string",
                position: 2,
              },
            ]
            : statusTitle === "Sold"
              ? [
                {
                  title: "Sold date",
                  dataIndex: "_computed_sold_date",
                  key: "_computed_sold_date",
                  type: "string",
                  position: 2,
                },
              ]
              : []),

          ...(statusTitle === "Sold"
            ? [
              {
                title: formatColumnTitle("$$$ PRICE CUT"),
                dataIndex: "_computed_sold_dollar_discount",
                key: "_computed_sold_dollar_discount",
                type: "number",
                align: "center",
                render: (val) => formatSignedCurrency(val),
                position: 2.1,
              },
              {
                title: formatColumnTitle("% PRICE CUT"),
                dataIndex: "_computed_sold_percent_discount",
                key: "_computed_sold_percent_discount",
                type: "number",
                align: "center",
                render: (val) => formatSignedPercent(val),
                position: 2.2,
              },
            ]
            : statusTitle === "Rented"
              ? [
                {
                  title: formatColumnTitle("$$$ PRICE CUT"),
                  dataIndex: "_computed_rented_dollar_discount",
                  key: "_computed_rented_dollar_discount",
                  type: "number",
                  align: "center",
                  render: (val) => formatSignedCurrency(val),
                  position: 2.1,
                },
                {
                  title: formatColumnTitle("% PRICE CUT"),
                  dataIndex: "_computed_rented_percent_discount",
                  key: "_computed_rented_percent_discount",
                  type: "number",
                  align: "center",
                  render: (val) => formatSignedPercent(val),
                  position: 2.2,
                },
              ]
              : []),

          {
            title: "BE",
            dataIndex: "bedrooms",
            key: "bedrooms",
            type: "number",
            position: 3,
          },
          {
            title: "BA",
            dataIndex: "bathrooms",
            key: "bathrooms",
            type: "number",
            position: 4,
          },
          {
            // formatColumnTitle splits on whitespace, so this renders "HLF" over "BA".
            title: formatColumnTitle("HLF BA"),
            dataIndex: "half_bathrooms",
            key: "half_bathrooms",
            type: "number",
            position: 5,
          },
          // Add HOME TYPE column when there are multiple home types
          ...(hasMultipleHomeTypes
            ? [
              {
                title: "HOME TYPE",
                dataIndex: "home_type",
                key: "home_type",
                type: "string",
                position: 5.5,
              },
            ]
            : []),
          {
            title: "LIVING SF",
            dataIndex: "sf_under_ac",
            key: "sf_under_ac",
            type: "number",
            position: 6,
          },
          ...(statusTitle === "Rent" || statusTitle === "Rented"
            ? [
              {
                title: "FURNITURE",
                dataIndex: "_computed_furniture",
                key: "_computed_furniture",
                type: "string",
                position: 6.5,
              },
            ]
            : []),
          {
            title: "WATER VIEW",
            dataIndex: "_computed_water_view",
            key: "_computed_water_view",
            type: "string",
            position: 7,
          },
          // Only show CVRD GAR for non-Condo-only homes
          ...(!isCondoOnly
            ? [
              {
                title: "CVRD GAR",
                dataIndex: "_computed_garage",
                key: "_computed_garage",
                type: "string",
                position: 8,
              },
            ]
            : []),
          // Only show PRVT POOL for non-Condo-only homes
          ...(!isCondoOnly
            ? [
              {
                title: "PRVT POOL",
                dataIndex: "_computed_pool",
                key: "_computed_pool",
                type: "string",
                position: 9,
              },
            ]
            : []),
          // Add TERM column for Rent and Rented statuses
          ...(statusTitle === "Rent" || statusTitle === "Rented"
            ? [
              {
                title: "TERM",
                dataIndex: "_computed_rental_prices",
                key: "_computed_rental_prices",
                type: "string",
                position: 10,
                nowrap: true,
              },
            ]
            : []),
          {
            title: (
              <div className="text-center">
                SHORT
                <br />
                ADDRESS
              </div>
            ),
            dataIndex: "_computed_address",
            key: "_computed_address",
            type: "string",
            position: 11,
          },
        ];

    if (statusTitle === "Sale" || statusTitle === "Rent") {
      columns = [
        ...columns,
        {
          title: (
            <span className="block text-center leading-tight">
              REQUEST
              <br />
              INFO/
              <br />
              SHOWING
            </span>
          ),
          dataIndex: "_request_info_showing",
          key: "_request_info_showing",
          align: "center",
          nowrap: true,
          render: (_val, property) => (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                openRequestInfoForm(property);
              }}
              className="rounded-md bg-[#e4d63b] px-3 py-1.5 text-xs font-extrabold text-black shadow-sm transition-colors hover:bg-[#d2c42a] focus:outline-none focus:ring-2 focus:ring-[#b3a71f] focus:ring-offset-1"
            >
              Request Info/Showing
            </button>
          ),
        },
      ];
    }

    const defaultGuide =
      community?.matrixGuideText ||
      matrixConfig?.matrixGuideText ||
      "Tap on property for more details, tap on column headers to sort tables";

    // Per-table guide text: look up specific text based on status, fall back to default
    const perTableGuideMap = {
      SALE: matrixConfig?.matrixGuideTextForSale,
      RENT: matrixConfig?.matrixGuideTextForRent,
      SOLD: matrixConfig?.matrixGuideTextSold,
      RENTED: matrixConfig?.matrixGuideTextRented,
    };
    const hasAnyPerTableGuide = Object.values(perTableGuideMap).some(
      (val) => val !== undefined && val !== null,
    );
    const guideValue = hasAnyPerTableGuide
      ? perTableGuideMap[statusUpper] ?? ""
      : defaultGuide;
    const guideText = String(guideValue || "").trim();

    const scrollRef = scrollRefMap[statusUpper] || null;
    const titleRef = titleRefMap[statusUpper] || null;
    const isDarkRed = statusUpper === "SOLD" || statusUpper === "RENTED";
    const statusColor = isDarkRed ? "dark" : "light";
    const headerText = getTableHeader();
    // The bottom rule closes the title bar off from the column headers below it —
    // without it the red bar and the header row read as one block.
    //
    // sticky: the bar floats while its own matrix is on screen, so you can always
    // tell which matrix you are reading (top offset computed below). z-50 clears
    // the table's frozen address columns, which run up to z-40; the frame around
    // the matrix is an isolated stacking context, so that number stays local and
    // the bar still passes under the page's filter bars on the way out.
    // border-t-2 lives on the title bar, not on the frame around it: the frame's
    // own top border scrolls out of view while this bar stays pinned, which left
    // the floating red bar with no black line above it. The frame below drops its
    // top border to match, so the two never double up when nothing is stuck.
    // flex: the matrix name sits on the left and its property count on the right.
    // On a narrow screen the count wraps to its own line, and ml-auto keeps it
    // right-justified there.
    const headerClass = `sticky z-50 flex flex-wrap items-center gap-x-2 gap-y-1 ${isDarkRed ? "bg-red-800" : "bg-red-600"} text-white font-bold text-lg p-2 border-t-2 border-b-2 border-black`;
    const propertyCount = enrichedData.length;
    const propertyCountLabel = `${propertyCount.toLocaleString("en-US")} ${propertyCount === 1 ? "Property" : "Properties"}`;
    const statusLinkMap = {
      sale: matrixConfig?.mls_sale_list_link,
      rent: matrixConfig?.mls_rental_list_link,
      sold: matrixConfig?.mls_sold_list_link,
      rented: matrixConfig?.mls_rented_list_link,
    };
    const listUrl = statusLinkMap[status.toLowerCase()] || null;

    // SOLD / RENTED subdivision selector — a copy of the global subdivision menu,
    // rendered right above this matrix and filtering only this matrix.
    const subdivisionDropdownControl = showMatrixSubdivision && subdivisionMenu === "DROP_DOWN" ? (
      <div className={FILTER_DROPDOWN_GROUP_CLASS}>
        <label className={FILTER_DROPDOWN_LABEL_CLASS}>
          SUBDIVISION
        </label>
        <select
          value={activeMatrixSubdivision}
          onChange={(e) => setThisMatrixSubdivision(e.target.value)}
          className={FILTER_DROPDOWN_SELECT_CLASS}
        >
          <option value="">ALL</option>
          {subdivisionDropdownOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    ) : null;

    const subdivisionButtonControls = showMatrixSubdivision && subdivisionMenu !== "DROP_DOWN" ? (
      <div className="flex flex-wrap justify-center items-center gap-1 mb-2">
        <button
          type="button"
          onClick={() => setThisMatrixSubdivision("")}
          className={`px-1.5 py-0.5 text-xs font-bold border-2 transition-colors min-w-[40px] ${!activeMatrixSubdivision
              ? ACTIVE_FILTER_BUTTON_CLASS
              : INACTIVE_FILTER_BUTTON_CLASS
            }`}
        >
          ALL
        </button>
        {subdivisionDropdownOptions.map((sub) => (
          <button
            key={sub.value}
            type="button"
            onClick={() => setThisMatrixSubdivision(sub.value)}
            className={`px-2 py-0.5 text-xs font-bold border-2 transition-colors min-w-[56px] ${activeMatrixSubdivision === sub.value
                ? ACTIVE_FILTER_BUTTON_CLASS
                : INACTIVE_FILTER_BUTTON_CLASS
              }`}
          >
            {sub.label.toUpperCase()}
          </button>
        ))}
      </div>
    ) : null;

    const subdivisionControls = showMatrixSubdivision ? (
      subdivisionMenu === "DROP_DOWN" ? (
        <div className={`${FILTER_DROPDOWN_ROW_CLASS} mb-2`}>
          {subdivisionDropdownControl}
        </div>
      ) : (
        subdivisionButtonControls
      )
    ) : null;

    // Only offer CURRENT YEAR (THIS_YEAR) when the community has enough current-year activity
    // (SOLD ≥ 5, RENTED ≥ 3); otherwise drop that option from this matrix's controls.
    const showCurrentYearOpt =
      statusUpper === "SOLD"
        ? showCurrentYearSold
        : statusUpper === "RENTED"
          ? showCurrentYearRented
          : true;
    const visibleDateRangeOptions = showCurrentYearOpt
      ? DATE_RANGE_OPTIONS
      : DATE_RANGE_OPTIONS.filter((opt) => opt.value !== "THIS_YEAR");

    // SOLD / RENTED time-range control — admin-toggled (matrixDateRangeMenu),
    // rendered right above its own matrix and filtering only that matrix.
    const dateRangeDropdownControl = showDateRange && dateRangeMenu === "DROP_DOWN" ? (
      <div className={FILTER_DROPDOWN_GROUP_CLASS}>
        <label className={FILTER_DROPDOWN_LABEL_CLASS}>
          {statusUpper === "SOLD" ? "SOLD PERIOD" : "RENTED PERIOD"}
        </label>
        <select
          value={activeDateRange}
          onChange={(e) => setThisDateRange(e.target.value)}
          className={FILTER_DROPDOWN_SELECT_CLASS}
        >
          {visibleDateRangeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    ) : null;

    const dateRangeButtonControls = showDateRange && dateRangeMenu !== "DROP_DOWN" ? (
      <div className="flex flex-wrap justify-center items-center gap-1 mb-2">
        {visibleDateRangeOptions.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setThisDateRange(opt.value)}
            className={`text-xs font-bold border-2 transition-colors ${opt.value === "ALL"
                ? "px-1.5 py-0.5 min-w-[40px]"
                : "px-2 py-0.5 min-w-[80px]"
              } ${activeDateRange === opt.value
                ? ACTIVE_FILTER_BUTTON_CLASS
                : INACTIVE_FILTER_BUTTON_CLASS
              }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    ) : null;

    const dateRangeControls = showDateRange ? (
      dateRangeMenu === "DROP_DOWN" ? (
        <div className={`${FILTER_DROPDOWN_ROW_CLASS} mb-2`}>
          {dateRangeDropdownControl}
        </div>
      ) : (
        dateRangeButtonControls
      )
    ) : null;

    const combinedDropdownControls =
      subdivisionDropdownControl || dateRangeDropdownControl ? (
        <div className={`${FILTER_DROPDOWN_ROW_CLASS} mb-2`}>
          {subdivisionDropdownControl}
          {dateRangeDropdownControl}
        </div>
      ) : null;

    // This matrix's own controls (SOLD/RENTED period and subdivision). They stick
    // directly under the global filter bar so it cannot cover them as the matrix
    // scrolls past, and only for as long as this matrix is on screen — the sticky
    // container is this block, so the next matrix's controls take over.
    const hasMatrixControls = Boolean(
      combinedDropdownControls || subdivisionControls || dateRangeControls,
    );

    // Where this matrix's title bar comes to rest: under the site header and the
    // global filter bar, plus this matrix's own controls when it has any (their
    // measured height — see getMatrixControlsRef).
    const matrixControlsHeight = hasMatrixControls
      ? matrixControlsHeights[statusUpper] || 0
      : 0;
    const headerStickyTop = `calc(var(--site-header-height, 0px) + var(--matrix-filter-height, 0px)${matrixControlsHeight ? ` + ${matrixControlsHeight}px` : ""})`;

    return (
      <div className="mb-10">
        {hasMatrixControls && (
          /* pb-2 is the gap between these floating controls and the title bar
             below them: this block's height is measured into headerStickyTop,
             so the padding pushes the sticky title bar down with it. It buys
             16px, not 8px — the padding also stops the last child's mb-2 from
             collapsing out of this box, so that margin now counts too. */
          <div
            ref={getMatrixControlsRef(statusUpper)}
            className="sticky z-20 bg-white pt-2 pb-2"
            style={{
              top: "calc(var(--site-header-height, 0px) + var(--matrix-filter-height, 0px))",
            }}
          >
            {combinedDropdownControls}
            {!combinedDropdownControls && subdivisionControls}
            {!combinedDropdownControls && dateRangeControls}
            {combinedDropdownControls && subdivisionButtonControls}
            {combinedDropdownControls && dateRangeButtonControls}
          </div>
        )}
        {enrichedData.length > 0 && guideText && (
          <div className="flex flex-wrap items-center gap-2 mb-1 px-1 text-black text-sm">
            <span>{highlightTapWords(guideText)}</span>
          </div>
        )}
        {/* Black frame around the red header bar and its table, so each matrix
            reads as one bounded block. isolate keeps the sticky title bar's
            z-index inside this frame: it has to outrank the table's frozen
            columns without outranking the page's own floating filter bars. */}
        <div className="border-x-2 border-b-2 border-black isolate">
          <h3 ref={titleRef} className={headerClass} style={{ top: headerStickyTop }}>
            {listUrl ? (
              <a href={listUrl} target="_blank" rel="noopener noreferrer" className="text-white no-underline hover:opacity-80">
                {headerText}
              </a>
            ) : (
              <span>{headerText}</span>
            )}
            {propertiesLoaded && (
              <span className="ml-auto text-white uppercase px-2 whitespace-nowrap">
                {propertyCountLabel}
              </span>
            )}
          </h3>
          {/* Scroll container — owns only the table's horizontal scroll */}
          <div className="overflow-x-auto scrollbar-hidden" ref={scrollRef}>
            <div className="w-fit min-w-full mx-auto pr-4">
              <CustomTable
                status={statusTitle}
                columns={columns}
                dataSource={enrichedData}
                onClickCell={(property, columnKey) => handleCellClick(property, columnKey)}
                linkableColumns={linkableColumns}
                showSortIcons={false}
                emptyMessage={getEmptyMessage()}
                noInternalScroll
                floatBelowRef={titleRef}
              />
            </div>
          </div>
        </div>
        <AlwaysVisibleScrollbar scrollContainerRef={scrollRef} statusColor={statusColor} />
      </div>
    );
  };

  /** Build modal details from ALL non-empty property fields (Excel/DB schema). */
  const generateDetails = (parsedData) => {
    const clean = (v) => {
      if (v === null || v === undefined) return null;
      const s = String(v).trim();
      if (s === "" || s.toLowerCase() === "none" || s.toUpperCase() === "N/A")
        return null;
      return s;
    };

    const formatValue = (val, field) => {
      if (val === null || val === undefined || val === "") return null;
      // Prisma Decimal and similar objects
      const raw =
        typeof val === "object" && val !== null && !(val instanceof Date)
          ? (val?.toString?.() ?? String(val))
          : val;
      if (field.format === "price") {
        const n =
          typeof raw === "number"
            ? raw
            : parseFloat(String(raw).replace(/[$,]/g, ""));
        return isNaN(n) ? raw : `$${Number(n).toLocaleString()}`;
      }
      if (field.format === "date") {
        try {
          return raw ? convertDate(raw) : null;
        } catch {
          return raw;
        }
      }
      if (Array.isArray(raw)) return raw.join(", ");
      return formatArrayField(raw);
    };

    const buildAddress = () => {
      const streetNumber = clean(parsedData.street_number);
      const compassDirection = clean(parsedData.compass_direction);
      const streetName = clean(parsedData.street_name);
      const postStreetDirection = clean(parsedData.post_street_direction);
      const streetParts = [streetNumber];
      if (compassDirection) streetParts.push(compassDirection);
      if (streetName) streetParts.push(streetName);
      if (postStreetDirection) streetParts.push(postStreetDirection);
      const line1 = streetParts.filter(Boolean).join(" ");
      const unit = clean(parsedData.unit_number)
        ? `Unit #: ${parsedData.unit_number}`
        : null;
      const city = clean(parsedData.city);
      const state = clean(parsedData.state);
      const zipCode = clean(parsedData.zip_code);
      const cityStateZip = [city, state, zipCode].filter(Boolean).join(" ");
      return [line1, unit, cityStateZip].filter(Boolean).join(", ") || null;
    };

    const getSubdivisionLabel = () => {
      const sub = parsedData.subdivision;
      if (!sub) return null;
      return sub.shortName || sub.fullName || null;
    };

    const details = [];
    for (const field of PROPERTY_DISPLAY_FIELDS) {
      let value;
      if (field.computed && field.key === "address") {
        value = buildAddress();
      } else if (field.computed && field.key === "_subdivision") {
        value = getSubdivisionLabel();
      } else {
        value = formatValue(parsedData[field.key], field);
      }
      if (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ""
      ) {
        details.push({
          id: field.key,
          label: field.label,
          value,
          position: field.position ?? 100,
        });
      }
    }
    return details.sort((a, b) => (a.position ?? 100) - (b.position ?? 100));
  };

  // Function to determine the correct display status
  const getDisplayStatus = (property) => {
    if (property.cw_listing_status === "SOLD") return "Sold";
    if (property.cw_listing_status === "RENTED") return "Rented";
    if (
      property.cw_listing_status === "ACTIVE" ||
      property.cw_listing_status === "PENDING"
    ) {
      if (property.listing_type === "Residential Rental") return "Rent";
      if (property.listing_type === "Residential Sale") return "Sale";
    }
    return property.status; // fallback
  };




  if (isLoading || siteLoading) {
    return (
      <div className="flex h-screen justify-center items-center">
        <i
          className="pi pi-spin pi-spinner text-red-700"
          style={{ fontSize: "2rem" }}
        ></i>
      </div>
    );
  }

  const activePromotions = [1, 2, 3, 4, 5]
    .map((slot) => ({
      slot,
      enabled: Boolean(matrixConfig?.[`promotion${slot}Enabled`]),
      text: matrixConfig?.[`promotion${slot}Text`]?.trim(),
      url: matrixConfig?.[`promotion${slot}Url`]?.trim(),
    }))
    .filter((promotion) => promotion.enabled && promotion.text);

  return (
    <div className="container flex flex-col items-center justify-center mx-auto w-full px-2 sm:px-4">
      <div className="flex flex-col justify-center max-w-6xl mx-auto w-full">
        {data != null && data.showDevBanner && (
          <h2 className="text-lg font-bold text-center mb-2 uppercase text-red-700">
            {data.devBannerMessage}
          </h2>
        )}
        <h1 className="text-2xl mb-1 text-center font-bold text-black uppercase">
          {pageHeader?.h1 || community?.name || "Welcome"}
        </h1>
        <p className="mb-2 text-gray-700 md:text-sm leading-relaxed text-center">
          {pageHeader?.tagline || "Discover your dream property"}
        </p>
        {activePromotions.length > 0 && (
          <div className="flex flex-col items-center gap-2 mb-0.5 w-full">
            {activePromotions.map(({ slot, text, url }) => {
              const promoStyle = PROMOTION_STYLE_BY_SLOT[slot];
              const promoClasses =
                "inline-flex self-center border-2 border-black text-center font-bold uppercase px-3 py-1 text-xs sm:text-sm";
              const promoSharedStyle = {
                ...promoStyle,
                textDecoration: "none",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.12)",
                width: "fit-content",
                maxWidth: "100%",
              };

              const promoTextStyle = {
                fontFamily: "Tahoma, Verdana, Arial, sans-serif",
                fontWeight: 800,
                letterSpacing: "0.01em",
              };

              if (!url) {
                return (
                  <div
                    key={slot}
                    className={promoClasses}
                    style={promoSharedStyle}
                  >
                    <span style={promoTextStyle}>{text}</span>
                  </div>
                );
              }

              return (
                <a
                  key={slot}
                  href={url}
                  className={`${promoClasses} transition-colors hover:opacity-95`}
                  style={promoSharedStyle}
                >
                  <span style={promoTextStyle}>{text}</span>
                </a>
              );
            })}
          </div>
        )}
        {/* No overflow here on purpose: a wrapper that scrolls in either axis
            becomes the containing block for the sticky filter bar below, which
            would pin it to this box instead of the viewport. Each matrix carries
            its own overflow-x-auto, so nothing needs to scroll at this level. */}
        <div className="leading-relaxed text-gray-700 w-full">
          {(() => {
            const m = matrixConfig;
            const homeTypeMenu = m.matrixHomeTypeMenu || "NONE";
            const subdivisionsMenu = m.matrixSubdivisionsMenu || "NONE";
            const listingCategoryMenu =
              m.matrixListingCategoryMenu || "BUTTONS";

            const homeTypeUseButtons = homeTypeMenu === "BUTTONS";
            const homeTypeHide = homeTypeMenu === "NONE";
            const subdivisionsUseButtons = subdivisionsMenu === "BUTTONS";
            const subdivisionsHide = subdivisionsMenu === "NONE";
            const statusUseButtons = listingCategoryMenu === "BUTTONS";
            const statusHide = listingCategoryMenu === "NONE";

            // Hiding the global subdivision row hands the control to each SOLD /
            // RENTED matrix; see topSubdivisionHidden where it is defined, next to
            // the flag renderTable reads.
            const hideTopSubdivision = topSubdivisionHidden;

            const getSubdivisionLabel = (s, forButtons) => {
              const key = forButtons
                ? m.subdivisionNameForButtons || "SHORT_NAME"
                : m.subdivisionNameForDropdown || "SHORT_NAME";
              if (key === "FULL_NAME") return s.fullName || s.shortName || "";
              if (key === "VERY_SHORT_NAME")
                return s.shortestName || s.shortName || s.fullName || "";
              return s.shortName || s.fullName || "";
            };

            const subdivisionOptions = (subdivisions || [])
              .filter((s) => {
                if (!allowedPropertyTypes || !s.homeType) return true;
                return allowedPropertyTypes.has(s.homeType.toUpperCase());
              })
              .map((s) => ({
                label: getSubdivisionLabel(s, subdivisionsUseButtons),
                value: s.shortName,
              }))
              .sort((a, b) =>
                a.label.localeCompare(b.label, undefined, {
                  sensitivity: "base",
                }),
              );

            // Pre-compute visible home type buttons when BUTTONS mode is enabled
            let visibleHomeTypeButtons = [];
            if (homeTypeUseButtons && !hasSingleHomeType && !homeTypeHide) {
              const allHomeTypeButtons = homeTypeDropdownOptions.filter(
                (opt) => opt.value !== "__SUBDIVISION__",
              );
              visibleHomeTypeButtons = allHomeTypeButtons;
              if (activeSubdivision) {
                const selectedSub = subdivisions.find(
                  (s) => s.shortName === activeSubdivision,
                );
                const subHomeType = selectedSub?.homeType
                  ? normalizeHomeType(selectedSub.homeType)
                  : null;

                const propsInSub = propertiesWithDefaults.filter(
                  (p) => p._subdivision === activeSubdivision,
                );
                const homeTypesInSub = new Set(
                  propsInSub.map((p) =>
                    normalizeHomeType(p.home_type).toUpperCase(),
                  ),
                );
                if (subHomeType) homeTypesInSub.add(subHomeType.toUpperCase());

                visibleHomeTypeButtons = allHomeTypeButtons.filter(
                  (opt) =>
                    opt.value === "ALL" ||
                    homeTypesInSub.has(opt.value.toUpperCase()),
                );
              }
            }

            const allUseButtons =
              homeTypeUseButtons && subdivisionsUseButtons && statusUseButtons;
            const allUseDropdowns =
              !homeTypeUseButtons &&
              !subdivisionsUseButtons &&
              !statusUseButtons;

            if (allUseButtons) {
              return (
                <div className="flex flex-col items-center gap-2.5 mb-3 sm:gap-1.5">
                  {/* Status / Category Buttons (FOR SALE / FOR RENT / SOLD / RENTED) — always first; hidden when matrixListingCategoryMenu is NONE */}
                  {!statusHide && (
                    <div className="flex flex-wrap justify-center gap-1">
                      {statusDropdownOptions.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setFilterStatus(opt.value)}
                          className={`text-xs font-bold border-2 transition-colors ${opt.value === "ALL"
                              ? "px-1.5 py-0.5 min-w-[40px]"
                              : "px-2 py-0.5 min-w-[56px]"
                            } ${filterStatus === opt.value
                              ? ACTIVE_FILTER_BUTTON_CLASS
                              : INACTIVE_FILTER_BUTTON_CLASS
                            }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Home Type Buttons - hidden when only one home type or matrixHomeTypeMenu is NONE */}
                  {!hasSingleHomeType && !homeTypeHide && (
                    <div className="flex flex-wrap justify-center gap-1">
                      {visibleHomeTypeButtons.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            setActiveHomeType(opt.value);
                            if (!activeSubdivision) setActiveSubdivision("");
                          }}
                          className={`text-xs font-bold border-2 transition-colors ${opt.value === "ALL"
                              ? "px-1.5 py-0.5 min-w-[40px]"
                              : "px-2 py-0.5 min-w-[56px]"
                            } ${activeHomeType === opt.value
                              ? ACTIVE_FILTER_BUTTON_CLASS
                              : INACTIVE_FILTER_BUTTON_CLASS
                            }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Subdivision Buttons - hidden when only one subdivision, subdivisions menu is NONE/DROP_DOWN, or narrowed to SOLD/RENTED (per-matrix selector takes over) */}
                  {hasSubdivisions &&
                    !hasSingleSubdivision &&
                    !subdivisionsHide &&
                    !hideTopSubdivision &&
                    subdivisionsUseButtons &&
                    subdivisionDropdownOptions.length > 0 && (
                      <div className="flex flex-wrap justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveHomeType("ALL");
                            setActiveSubdivision("");
                          }}
                          className={`px-1.5 py-0.5 text-xs font-bold border-2 transition-colors min-w-[40px] ${!activeSubdivision
                              ? ACTIVE_FILTER_BUTTON_CLASS
                              : INACTIVE_FILTER_BUTTON_CLASS
                            }`}
                        >
                          ALL
                        </button>
                        {subdivisionDropdownOptions.map((sub) => (
                          <button
                            key={sub.value}
                            type="button"
                            onClick={() => {
                              if (
                                activeHomeType === "__SUBDIVISION__" &&
                                activeSubdivision === sub.value
                              ) {
                                setActiveHomeType("ALL");
                                setActiveSubdivision("");
                              } else {
                                setActiveHomeType("__SUBDIVISION__");
                                setActiveSubdivision(sub.value);
                              }
                            }}
                            className={`px-2 py-0.5 text-xs font-bold border-2 transition-colors min-w-[56px] ${activeHomeType === "__SUBDIVISION__" &&
                                activeSubdivision === sub.value
                                ? ACTIVE_FILTER_BUTTON_CLASS
                                : INACTIVE_FILTER_BUTTON_CLASS
                              }`}
                          >
                            {sub.label.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    )}
                </div>
              );
            }

            // Mixed mode: render each filter independently according to its menu type.
            // Button rows stack vertically; dropdown rows sit side-by-side.
            const buttonFilters = [];
            const dropdownFilters = [];

            // Status / Category filter (FOR SALE / FOR RENT / SOLD / RENTED) — pushed first so it leads its group (category row first)
            if (!statusHide) {
              if (statusUseButtons) {
                buttonFilters.push(
                  <div
                    key="st-btn"
                    className="flex flex-wrap justify-center gap-1"
                  >
                    {statusDropdownOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setFilterStatus(opt.value)}
                        className={`text-xs font-bold border-2 transition-colors ${opt.value === "ALL"
                            ? "px-1.5 py-0.5 min-w-[40px]"
                            : "px-2 py-0.5 min-w-[56px]"
                          } ${filterStatus === opt.value
                            ? ACTIVE_FILTER_BUTTON_CLASS
                            : INACTIVE_FILTER_BUTTON_CLASS
                          }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>,
                );
              } else {
                dropdownFilters.push(
                  <div
                    key="st-dd"
                    className={FILTER_DROPDOWN_GROUP_CLASS}
                  >
                    <label className={FILTER_DROPDOWN_LABEL_CLASS}>
                      HOME STATUS
                    </label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className={FILTER_DROPDOWN_SELECT_CLASS}
                    >
                      {statusDropdownOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>,
                );
              }
            }

            // Home type filter
            if (!hasSingleHomeType && !homeTypeHide) {
              if (homeTypeUseButtons) {
                buttonFilters.push(
                  <div
                    key="ht-btn"
                    className="flex flex-wrap justify-center gap-1"
                  >
                    {visibleHomeTypeButtons.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setActiveHomeType(opt.value);
                          if (!activeSubdivision) setActiveSubdivision("");
                        }}
                        className={`text-xs font-bold border-2 transition-colors ${opt.value === "ALL"
                            ? "px-1.5 py-0.5 min-w-[40px]"
                            : "px-2 py-0.5 min-w-[56px]"
                          } ${activeHomeType === opt.value
                            ? ACTIVE_FILTER_BUTTON_CLASS
                            : INACTIVE_FILTER_BUTTON_CLASS
                          }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>,
                );
              } else {
                dropdownFilters.push(
                  <div
                    key="ht-dd"
                    className={FILTER_DROPDOWN_GROUP_CLASS}
                  >
                    <label className={FILTER_DROPDOWN_LABEL_CLASS}>
                      HOME TYPE
                    </label>
                    <select
                      value={activeHomeType}
                      onChange={(e) => {
                        setActiveHomeType(e.target.value);
                        setActiveSubdivision("");
                      }}
                      className={FILTER_DROPDOWN_SELECT_CLASS}
                    >
                      {homeTypeDropdownOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>,
                );
              }
            }

            // Subdivision filter (top/global) — suppressed in the SOLD/RENTED-narrowed view
            // so it doesn't duplicate the per-matrix subdivision selector above the table.
            if (
              hasSubdivisions &&
              !hasSingleSubdivision &&
              !subdivisionsHide &&
              !hideTopSubdivision &&
              subdivisionDropdownOptions.length > 0
            ) {
              if (subdivisionsUseButtons) {
                buttonFilters.push(
                  <div
                    key="sub-btn"
                    className="flex flex-wrap justify-center gap-1"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setActiveHomeType("ALL");
                        setActiveSubdivision("");
                      }}
                      className={`px-1.5 py-0.5 text-xs font-bold border-2 transition-colors min-w-[40px] ${!activeSubdivision
                          ? ACTIVE_FILTER_BUTTON_CLASS
                          : INACTIVE_FILTER_BUTTON_CLASS
                        }`}
                    >
                      ALL
                    </button>
                    {subdivisionDropdownOptions.map((sub) => (
                      <button
                        key={sub.value}
                        type="button"
                        onClick={() => {
                          if (
                            activeHomeType === "__SUBDIVISION__" &&
                            activeSubdivision === sub.value
                          ) {
                            setActiveHomeType("ALL");
                            setActiveSubdivision("");
                          } else {
                            setActiveHomeType("__SUBDIVISION__");
                            setActiveSubdivision(sub.value);
                          }
                        }}
                        className={`px-2 py-0.5 text-xs font-bold border-2 transition-colors min-w-[56px] ${activeHomeType === "__SUBDIVISION__" &&
                            activeSubdivision === sub.value
                            ? ACTIVE_FILTER_BUTTON_CLASS
                            : INACTIVE_FILTER_BUTTON_CLASS
                          }`}
                      >
                        {sub.label.toUpperCase()}
                      </button>
                    ))}
                  </div>,
                );
              } else {
                dropdownFilters.push(
                  <div
                    key="sub-dd"
                    className={FILTER_DROPDOWN_GROUP_CLASS}
                  >
                    <label className={FILTER_DROPDOWN_LABEL_CLASS}>
                      SUBDIVISION
                    </label>
                    <select
                      value={activeSubdivision}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val) {
                          setActiveHomeType("__SUBDIVISION__");
                          setActiveSubdivision(val);
                        } else {
                          setActiveHomeType("ALL");
                          setActiveSubdivision("");
                        }
                      }}
                      className={FILTER_DROPDOWN_SELECT_CLASS}
                    >
                      <option value="">ALL</option>
                      {subdivisionDropdownOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>,
                );
              }
            }

            // Every menu can be switched off in admin, and an empty bar would
            // still paint its background and rule as a stray strip.
            if (buttonFilters.length === 0 && dropdownFilters.length === 0) {
              return null;
            }

            return (
              // Floats below the site header as the matrices scroll past, so the
              // filters stay reachable without scrolling back up. z-index sits
              // under the header (z-50) and over the tables.
              <div
                ref={setFilterBarRef}
                className="sticky z-30 flex flex-col gap-2.5 mb-3 w-full sm:gap-1.5 bg-white py-2 border-b-2 border-black"
                style={{ top: "var(--site-header-height, 0px)" }}
              >
                {buttonFilters}
                {dropdownFilters.length > 0 && (
                  <div className={FILTER_DROPDOWN_ROW_CLASS}>
                    {dropdownFilters}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Matrix order: FOR SALE, FOR RENT, SOLD, RENTED.
              SOLD/RENTED time-range controls now render inside renderTable, right above each matrix. */}
          {(() => {
            const statusKeys =
              filterStatus === "ALL" ? MATRIX_ORDER : [filterStatus];

            if (activeHomeType === "__SUBDIVISION__") {
              if (!activeSubdivision) {
                return (
                  <p className="text-red-600 font-semibold italic py-4">
                    Select a subdivision above to view matrices.
                  </p>
                );
              }
              return statusKeys.map((statusKey) => (
                <React.Fragment key={statusKey}>
                  {renderTable(
                    statusKey,
                    filteredProperties,
                    activeSubdivision,
                  )}
                </React.Fragment>
              ));
            }

            if (activeHomeType === "ALL") {
              const showSubCol = hasSubdivisions && subdivisions.length > 1;
              return statusKeys.map((statusKey) => (
                <React.Fragment key={statusKey}>
                  {renderTable(statusKey, filteredProperties, null, {
                    showSubdivisionColumn: showSubCol,
                  })}
                </React.Fragment>
              ));
            }

            const allOfTypeLabel = (() => {
              const t = activeHomeType.toUpperCase();
              if (t === "SFH") return "ALL SFHs";
              if (t === "CONDO") return "ALL CONDOs";
              if (t === "TH") return "ALL THs";
              if (t === "VILLA") return "ALL Villas";
              if (t === "COACHHOME") return "ALL Coach Homes";
              return `ALL ${activeHomeType}s`;
            })();
            const subsOfType =
              subdivisions.filter(
                (s) =>
                  normalizeHomeType(s.homeType).toUpperCase() ===
                  activeHomeType.toUpperCase(),
              ) || [];
            const showSubdivisionColumn = subsOfType.length > 1;

            return statusKeys.map((statusKey) => (
              <React.Fragment key={statusKey}>
                {renderTable(statusKey, filteredProperties, null, {
                  allOfTypeLabel,
                  showSubdivisionColumn,
                })}
              </React.Fragment>
            ));
          })()}
        </div>
        {/* Modal removed — navigation is handled per-cell click */}
      </div>
    </div>
  );
};

export default Table;
